enum SidebarPosition {
  Absolute = "absolute",
  Fixed = "fixed",
  Static = "static",
}

const FIXED_TOP = 121;
const BOTTOM_SPACE = 30;
const SPACE_TO_FOOTER = 260;
const BREAKPOINT_MOBILE = 1024;

/**
 * Manage the position of the filter sidebar
 *
 * The form floats along the screen on desktop devices, making sure that smaller viewports can still scroll through the
 * sidebar.
 *
 * @param jobsForm The `form` element
 */
class FlyingForm {
  readonly jobsForm: HTMLFormElement;

  private sidebarPosition = SidebarPosition.Static;

  private scrollPos = window.scrollY;

  private scrollingDown = true;

  constructor(jobsForm: HTMLFormElement) {
    this.jobsForm = jobsForm;
  }

  /**
   * Set the min-height of the sidebar container, run the handler once to initialize the sidebar state and add it has
   * handler to the `scroll` and screen orientation events.
   */
  init(): void {
    this.calculateFormMinHeight();

    // add the handler to the scroll and screen orientation change events
    window.addEventListener("scroll", () => this.handleScrollEvent());
    window.screen.orientation.addEventListener("change", () => this.handleScrollEvent());

    // add the handler to the htmx:swapEvent
    window.addEventListener("htmx:afterSettle", () => this.handleListUpdate());

    // run it once on mount
    this.handleScrollEvent();
  }

  private setSidebarPosition(pos: SidebarPosition): void {
    this.jobsForm.style.position = pos;
    this.sidebarPosition = pos;
  }

  private setSidebarTop(top: number): void {
    this.jobsForm.style.top = `${top}px`;
  }

  /**
   * Handle the scroll event to correct the positioning of the form in the sidebar
   */
  private handleScrollEvent(): void {
    // check if we are mobile
    if (window.innerWidth < BREAKPOINT_MOBILE || window.screen.orientation.type.startsWith("portrait")) {
      this.setSidebarPosition(SidebarPosition.Static);
      return;
    }

    const formBoundingRect = this.jobsForm.getBoundingClientRect();

    if (formBoundingRect.height < window.innerHeight - (this.jobsForm.parentElement.offsetTop)) {
      // the form is smaller than the viewport, we don't need scrolling
      this.setSidebarPosition(SidebarPosition.Fixed);
    } else if (this.scrollPos < window.scrollY) {
      this.handleScrollingDown(formBoundingRect);
    } else if (this.scrollPos > window.scrollY) {
      this.handleScrollingUp(formBoundingRect);
    }

    this.scrollPos = window.scrollY;
  }

  /**
   * Handle scrolling down. Make sure that we don't scroll over the footer
   *
   * @param formBoundingRect
   */
  private handleScrollingDown(formBoundingRect: DOMRect): void {
    if (this.scrollingDown && document.body.scrollHeight - window.scrollY - window.innerHeight < SPACE_TO_FOOTER) {
      // handle user reaching the bottom of the page (we need to position the sidebar above the footer)
      if (this.sidebarPosition !== SidebarPosition.Absolute) {
        this.setSidebarTop(document.body.scrollHeight - formBoundingRect.height - SPACE_TO_FOOTER);
        this.setSidebarPosition(SidebarPosition.Absolute);
      }
    } else if (this.scrollingDown) {
      // handle normal scrolling down
      if (
        window.innerHeight - formBoundingRect.bottom >= BOTTOM_SPACE - 10
        && this.sidebarPosition !== SidebarPosition.Fixed
      ) {
        this.setSidebarTop(window.innerHeight - formBoundingRect.height - BOTTOM_SPACE);
        this.setSidebarPosition(SidebarPosition.Fixed);
      }
    } else if (this.sidebarPosition !== SidebarPosition.Absolute) {
      this.setSidebarTop(window.scrollY + formBoundingRect.top);
      this.setSidebarPosition(SidebarPosition.Absolute);
    }

    this.scrollingDown = true;
  }

  /**
   * Handle scrolling up
   *
   * @param formBoundingRect
   */
  private handleScrollingUp(formBoundingRect: DOMRect): void {
    if (this.scrollingDown && this.sidebarPosition !== SidebarPosition.Absolute) {
      this.setSidebarTop(window.scrollY + this.jobsForm.offsetTop);
      this.setSidebarPosition(SidebarPosition.Absolute);
    } else if (this.sidebarPosition !== SidebarPosition.Fixed && formBoundingRect.top >= FIXED_TOP) {
      this.setSidebarTop(FIXED_TOP);
      this.setSidebarPosition(SidebarPosition.Fixed);
    }

    this.scrollingDown = false;
  }

  private calculateFormMinHeight(): void {
    this.jobsForm.parentElement.style.minHeight = `${this.jobsForm.clientHeight + FIXED_TOP}px`;
  }

  private handleListUpdate(): void {
    this.calculateFormMinHeight();
  }
}

export default FlyingForm;
